import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ProcessMenu } from './ProcessMenu.js';
import { useCustomer } from '../customer-context.js';

/**
 * Cross-navigation between the security screens, per the customer's product
 * decisions on issues #101, #397, and #609 (@dylan219).
 *
 * Issue #609 (@dylan219 2026-08-10 ruling) REVERSES two prior decisions:
 *   - #397 option A (MR !584): high pages now also show the medium pages (the
 *     prior high-only restriction is removed — an agent stranded on a high page
 *     can navigate back to a medium page from the same dropdown).
 *   - #455 (alphabetical sort): the fixed canonical order below REPLACES the
 *     A→Z alphabetical sort; every security page — medium AND high — now renders
 *     entries in the same fixed order.
 *
 * The canonical order (applied to ALL security pages, medium and high alike):
 *   1.  EDS Medium Security        (SCRN-03)
 *   2.  VISA Medium Security       (SCRN-04)
 *   3.  Business Medium Security   (SCRN-05)
 *   4.  ECL Medium Security        (SCRN-54)
 *   5.  Broker Security            (SCRN-58, #425)
 *   6.  EDS High Security          (SCRN-07)
 *   7.  VISA High Security         (SCRN-08)
 *   8.  Mortgage Security          (SCRN-09)
 *   9.  Business High Security     (SCRN-10)
 *  10.  ECL High Security          (SCRN-55)
 *  11.  Authorised VISA User       (SCRN-06)
 *  12.  Hold Information           (SCRN-42)
 *  13.  Customer Notes             (SCRN-14)
 *  14.  Customer dashboard — HIGH pages ONLY, and only when medium security has
 *       already been completed on this session (securityLevel 'medium'|'high').
 *
 * The current page's own entry is EXCLUDED from its own dropdown (a page never
 * offers to navigate to itself).
 *
 * The dropdown is PURE navigation: every entry targets an existing, RBAC-gated
 * route, and the router re-checks the screen permission on arrival
 * (deny-by-default). A menu entry can never grant or bypass access.
 */

/** A medium security page that participates in the cross-navigation menu. */
export interface MediumSecurityPage {
  /** Stable id (the RBAC screen id), used as the cross-nav key. */
  readonly id: string;
  /** The menu label shown for navigating TO this page from another. */
  readonly label: string;
  /** The route for this medium page. */
  readonly route: string;
  /** The label of THIS page's own relevant high security option. */
  readonly highLabel: string;
  /** The route of THIS page's own relevant (same-family) high page. */
  readonly highRoute: string;
}

/**
 * The medium security pages, each mapped to its OWN relevant (same-family) high
 * security page (issue #101). Order matches the canonical fixed order (#609).
 *   - EDS Medium    (SCRN-03) → EDS High    (SCRN-07)
 *   - VISA Medium   (SCRN-04) → VISA High   (SCRN-08)
 *   - BUS24 Medium  (SCRN-05) → BUS24 High  (SCRN-10)
 *   - ECL Medium    (SCRN-54) → ECL High    (SCRN-55)
 */
export const MEDIUM_SECURITY_PAGES: readonly MediumSecurityPage[] = [
  {
    id: 'SCRN-03',
    label: 'EDS Medium Security',
    route: '/security/medium',
    highLabel: 'EDS High Security',
    highRoute: '/security/eds-high',
  },
  {
    id: 'SCRN-04',
    label: 'VISA Medium Security',
    route: '/security/visa-medium',
    highLabel: 'VISA High Security',
    highRoute: '/security/visa-high',
  },
  {
    id: 'SCRN-05',
    label: 'Business Medium Security',
    route: '/security/bus24-medium',
    highLabel: 'Business High Security',
    highRoute: '/security/bus24-high',
  },
  {
    id: 'SCRN-54',
    label: 'ECL Medium Security',
    route: '/security/ecl-medium',
    highLabel: 'ECL High Security',
    highRoute: '/security/ecl-high',
  },
];

/** A high security page that participates in the cross-navigation menu. */
export interface HighSecurityPage {
  /** Stable id (the RBAC screen id), used as the cross-nav key. */
  readonly id: string;
  /** The menu label shown for navigating TO this page from another. */
  readonly label: string;
  /** The route for this high page. */
  readonly route: string;
}

/**
 * The high security pages. Order matches the canonical fixed order (#609).
 *   - EDS High         (SCRN-07) → /security/eds-high
 *   - VISA High        (SCRN-08) → /security/visa-high
 *   - Mortgage Security(SCRN-09) → /security/mortgage
 *   - Business 24 High (SCRN-10) → /security/bus24-high
 *   - ECL High         (SCRN-55) → /security/ecl-high
 *
 * Mortgage Security is itself a high page (CCF-24) — it belongs in this list so
 * it shows on every OTHER security page and is excluded on its own page.
 *
 * Note: the canonical order places Mortgage BEFORE Business High and ECL High
 * (positions 8/9/10 in the full 14-entry list), reflecting the #609 ruling.
 */
export const HIGH_SECURITY_PAGES: readonly HighSecurityPage[] = [
  { id: 'SCRN-07', label: 'EDS High Security', route: '/security/eds-high' },
  { id: 'SCRN-08', label: 'VISA High Security', route: '/security/visa-high' },
  { id: 'SCRN-09', label: 'Mortgage Security', route: '/security/mortgage' },
  {
    id: 'SCRN-10',
    label: 'Business High Security',
    route: '/security/bus24-high',
  },
  { id: 'SCRN-55', label: 'ECL High Security', route: '/security/ecl-high' },
];

/** A single, navigable option in the security process menu. */
export interface SecurityMenuOption {
  readonly label: string;
  readonly route: string;
}

/**
 * Mortgage Security (SCRN-09, CCF-24) has no "medium" counterpart — per the
 * story, a mortgage customer swivels straight to a high-security Hogan
 * questionnaire + 2FA, there is no medium-risk mortgage variant. It is a
 * first-class member of {@link HIGH_SECURITY_PAGES} and therefore participates
 * in the unified builder — no separate constant is needed to add it. Exported
 * for test assertions only.
 */
export const MORTGAGE_SECURITY_OPTION: SecurityMenuOption = {
  label: 'Mortgage Security',
  route: '/security/mortgage',
};

// Note: Mortgage Security (SCRN-09) needs no self-exclusion constant — unlike
// Broker Security / Authorised VISA User (standalone options), it is a member of
// HIGH_SECURITY_PAGES and is excluded from its own page by the generic
// `page.id !== currentId` filter in the unified builder's high-pages loop.

/**
 * Broker Medium Security (SCRN-58, CCF-318 / #425). Per @dylan219's #425
 * ruling (2026-07-21), EVERY security screen's cross-navigation dropdown offers
 * the Broker Medium Security page — NOT a direct link to Reset Internet
 * Password. The earlier direct `/reset-internet-password` link was wrong: the
 * agent must "complete the broker security pages first", and at the bottom of
 * broker security (once it passes) the flow "brings you directly to internet
 * password reset" (SCRN-57).
 *
 * Per #609 (@dylan219 ruling C, 2026-08-10): Broker Security appears on EVERY
 * security page (medium AND high); the bug-reporter omitted it by oversight.
 * It is placed in the medium group of the canonical fixed order (position 5,
 * adjacent to the other medium entries and before the high entries) — the most
 * logical position for a medium-security variant.
 *
 * Like every other entry, this is PURE navigation to a built, RBAC-gated route
 * (`/security/broker-medium`, re-checked on arrival, deny-by-default). Offered
 * on every page (medium and high) except its own page (SCRN-58) — a page must
 * never offer to navigate to itself.
 */
export const BROKER_MEDIUM_SECURITY_OPTION: SecurityMenuOption = {
  label: 'Broker Security',
  route: '/security/broker-medium',
};

/**
 * The RBAC screen id of the Broker Medium Security page itself (SCRN-58). Like
 * Mortgage Security (SCRN-09), the Broker medium page renders this cross-nav
 * dropdown (#474) so it must be excluded from its OWN dropdown.
 */
const BROKER_MEDIUM_SECURITY_SCREEN_ID = 'SCRN-58';

/**
 * Authorised VISA User (SCRN-06, CCF-25). A standalone TSYS-swivel security
 * variant where the agent enters the customer's 16-digit VISA card number — it
 * has no medium→high mapping and is not a member of {@link HIGH_SECURITY_PAGES},
 * so (like Broker Security) it is surfaced directly. Offered on EVERY other
 * security screen — medium and high — so an agent can swivel to it.
 */
export const AUTHORISED_VISA_USER_OPTION: SecurityMenuOption = {
  label: 'Authorised VISA User',
  route: '/security/visa-authorised-user',
};

/** The RBAC screen id of the Authorised VISA User page itself (excluded from its
 * own dropdown so it never offers to navigate to itself). */
const AUTHORISED_VISA_USER_SCREEN_ID = 'SCRN-06';

/**
 * The Customer dashboard route. Shown on HIGH security pages ONLY, and only
 * when medium security has already been completed on this session (issue #609,
 * @dylan219 2026-08-10).
 */
export const CUSTOMER_DASHBOARD_OPTION: SecurityMenuOption = {
  label: 'Customer dashboard',
  route: '/dashboard',
};

/**
 * Servicing entries offered on EVERY security screen's cross-navigation
 * dropdown, for consistency across the medium AND high pages (issue #222 part 3
 * / #101). Previously only the EDS Medium (SCRN-03) page surfaced these, so the
 * VISA Medium / VISA High / Business 24 Medium pages were missing "Hold
 * Information" and "Customer Notes"; baking them in here keeps every security
 * dropdown consistent.
 *
 * Both are BUILT, RBAC-gated routes — "Hold Information" → the Account Holds
 * screen (SCRN-42, CCF-79) and "Customer Notes" → the Customer Notes screen
 * (SCRN-14). "Customer Notes" is a real navigation now (issue #221): the earlier
 * "…is not yet available" toast was stale — the screen exists.
 *
 * Issue #237 (relaxes #222 for Customer Notes) + issue #454 (relaxes it for
 * Hold Information): per @dylan219's 2026-07-01 ruling Customer Notes, and per
 * @dylan219's 2026-07-22 ruling Hold Information, must be reachable AND usable
 * from these security screens EVEN when the customer has not yet completed
 * medium security — so a pre-medium jump to either no longer lands on the
 * "complete security first" prompt; both work at the identified stage (see
 * CustomerNotesScreen / AccountHoldsScreen). For Hold Information (#454) the
 * jump carries `{ fromSecurity, returnTo }` route state (the ORIGINATING
 * security page) so the Account Holds screen reads holds via the identified-
 * gated `/security/holds` twin and its "Back to security" control returns the
 * agent to the exact security page they came from (mirrors #358's "Check
 * Transactions" → "Back to High Security" round trip).
 */
export const SECURITY_SERVICING_OPTIONS: readonly SecurityMenuOption[] = [
  { label: 'Hold Information', route: '/accounts/holds' },
  { label: 'Customer Notes', route: '/customer-notes' },
];

/**
 * The Customer Notes route (SCRN-14). Issue #663 (CCRS-3448): when "Customer
 * Notes" is chosen FROM a security page, the navigation carries
 * `{ fromSecurity, returnTo, securityScreenId, securityMode }` route state so the
 * Customer Notes screen renders THIS security process menu (letting the agent
 * jump back into the security flow) instead of the default servicing process
 * menu, and its "Back to security" affordance returns to the exact originating
 * security page. This mirrors #454's "Hold Information" round trip. Exported so
 * tests can assert the navigation shape without re-deriving the literal.
 */
export const CUSTOMER_NOTES_ROUTE = '/customer-notes';

/**
 * The Account Holds route (SCRN-42). Issue #454: when "Hold Information" is
 * chosen from a security page, the navigation carries `{ fromSecurity, returnTo }`
 * route state so the Account Holds screen reads holds at the IDENTIFIED level
 * (`/security/holds` twin) and offers a "Back to security" control that returns
 * the agent to the originating security page. Exported so tests can assert the
 * navigation shape without re-deriving the literal.
 */
export const HOLDS_ROUTE = '/accounts/holds';

/**
 * Builds the canonical fixed-order options for ANY security page (medium OR
 * high), as per the #609 ruling (@dylan219 2026-08-10).
 *
 * ALL pages (medium and high alike) offer:
 *   Medium pages: EDS Medium → VISA Medium → Business Medium → ECL Medium
 *   Broker Security (position 5, adjacent to medium entries)
 *   High pages: EDS High → VISA High → Mortgage → Business High → ECL High
 *   Authorised VISA User (standalone)
 *   Servicing entries (Hold Information → Customer Notes)
 *
 * The current page is EXCLUDED from its own dropdown (it never offers to
 * navigate to itself). Each option is a `{ label, route }` wired to an
 * existing RBAC-gated route.
 *
 * This unified builder replaces the separate `buildSecurityMenuOptions` /
 * `buildHighSecurityMenuOptions` pair (which implemented the now-reversed #397
 * option A and #455 alpha sort). Both old names are kept as re-exports for
 * backwards compatibility with callers.
 *
 * @param currentId  RBAC screen id of the page currently being rendered.
 * @param mode       `'medium'` or `'high'` — used only to decide whether the
 *                   "Customer dashboard" entry is a candidate (high pages only).
 * @param mediumComplete  True when the customer has already completed medium
 *                   security on this session (securityLevel 'medium'|'high').
 *                   When true and `mode === 'high'`, a "Customer dashboard"
 *                   entry is appended after the servicing entries.
 * @param extra      Additional navigable options (rare; appended after
 *                   servicing entries and before the dashboard entry).
 */
export function buildUnifiedSecurityMenuOptions(
  currentId: string,
  mode: 'medium' | 'high' = 'medium',
  mediumComplete = false,
  extra: readonly SecurityMenuOption[] = [],
): SecurityMenuOption[] {
  const options: SecurityMenuOption[] = [];

  // 1–4: Medium pages (excluding the current page).
  for (const page of MEDIUM_SECURITY_PAGES) {
    if (page.id !== currentId) {
      options.push({ label: page.label, route: page.route });
    }
  }

  // 5: Broker Security — excluded only on the Broker medium page itself (#474).
  if (currentId !== BROKER_MEDIUM_SECURITY_SCREEN_ID) {
    options.push(BROKER_MEDIUM_SECURITY_OPTION);
  }

  // 6–10: High pages (excluding the current page).
  for (const page of HIGH_SECURITY_PAGES) {
    if (page.id !== currentId) {
      options.push({ label: page.label, route: page.route });
    }
  }

  // 11: Authorised VISA User (excluded on its own page).
  if (currentId !== AUTHORISED_VISA_USER_SCREEN_ID) {
    options.push(AUTHORISED_VISA_USER_OPTION);
  }

  // 12–13: Servicing entries (Hold Information, Customer Notes) + any extra.
  // De-duplicate extra vs the canonical servicing entries so a caller that
  // still passes one explicitly does not produce a duplicate.
  const servicing = SECURITY_SERVICING_OPTIONS.filter(
    (s) => !extra.some((e) => e.label === s.label),
  );
  options.push(...servicing);
  options.push(...extra);

  // 14: Customer dashboard — HIGH pages only, and only when medium is complete.
  if (mode === 'high' && mediumComplete) {
    options.push(CUSTOMER_DASHBOARD_OPTION);
  }

  return options;
}

/**
 * Builds options for a MEDIUM security page (legacy name — now delegates to
 * {@link buildUnifiedSecurityMenuOptions} with `mode: 'medium'`).
 *
 * Retained for backwards compatibility with existing callers and tests.
 */
export function buildSecurityMenuOptions(
  currentId: string,
  extra: readonly SecurityMenuOption[] = [],
): SecurityMenuOption[] {
  return buildUnifiedSecurityMenuOptions(currentId, 'medium', false, extra);
}

/**
 * Builds options for a HIGH security page (legacy name — now delegates to
 * {@link buildUnifiedSecurityMenuOptions} with `mode: 'high'`).
 *
 * Issue #609 reversal: this now also includes the medium pages (the prior
 * #397 option A restriction is removed). The `mediumComplete` flag controls
 * whether the "Customer dashboard" entry is appended.
 *
 * Retained for backwards compatibility with existing callers and tests.
 */
export function buildHighSecurityMenuOptions(
  extra: readonly SecurityMenuOption[] = [],
  currentId?: string,
  mediumComplete = false,
): SecurityMenuOption[] {
  return buildUnifiedSecurityMenuOptions(
    currentId ?? '',
    'high',
    mediumComplete,
    extra,
  );
}

/**
 * The shared options dropdown for the security screens (medium + high). Renders
 * the {@link ProcessMenu} built from {@link buildUnifiedSecurityMenuOptions}
 * plus the consistent servicing entries
 * ({@link SECURITY_SERVICING_OPTIONS}: Hold Information / Customer Notes) and
 * navigates to the selected option's route. Caller-supplied `noRouteOptions` are
 * listed for design fidelity but have no destination screen — they surface an
 * informational "…is not yet available" toast rather than navigating nowhere.
 *
 * Issue #609: the fixed canonical order (14 entries) is applied to ALL pages —
 * medium AND high. The #455 alphabetical sort is removed. On HIGH pages, if the
 * customer has already completed medium security this session, a "Customer
 * dashboard" entry is shown at the end of the list.
 */
export function SecurityProcessMenu({
  currentId,
  mode = 'medium',
  extra = [],
  noRouteOptions = [],
}: {
  /** The RBAC screen id of the CURRENT page. */
  currentId: string;
  /**
   * Which cross-navigation set to render:
   *   - `'medium'` (default): shows all other security pages EXCEPT a
   *     "Customer dashboard" entry (the customer may not have completed
   *     medium security yet).
   *   - `'high'`: shows all other security pages, PLUS a "Customer dashboard"
   *     entry when the customer has already completed medium security (#609).
   */
  mode?: 'medium' | 'high';
  /** Extra navigable options appended after the servicing entries. */
  extra?: readonly SecurityMenuOption[];
  /**
   * Options that are listed for design fidelity but have no destination screen:
   * they surface an informational "…is not yet available" toast rather than
   * navigating nowhere. (No security screen uses this today — the servicing
   * entries are all real routes now, issue #221/#222 — but the capability is
   * retained for any future design-only entry.)
   */
  noRouteOptions?: readonly string[];
}): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // Issue #609: on HIGH pages, include "Customer dashboard" only when medium
  // security has already been completed (securityLevel 'medium' or 'high').
  const { customer } = useCustomer();
  const mediumComplete =
    mode === 'high' &&
    (customer.securityLevel === 'medium' || customer.securityLevel === 'high');

  const navOptions = buildUnifiedSecurityMenuOptions(
    currentId,
    mode,
    mediumComplete,
    extra,
  );

  const routeByLabel = new Map(navOptions.map((o) => [o.label, o.route]));
  // Issue #609: the canonical fixed order replaces the #455 alphabetical sort.
  // The labels list is already in the correct fixed order from the builder.
  const labels = [...navOptions.map((o) => o.label), ...noRouteOptions];

  return (
    <ProcessMenu
      options={labels}
      onSelect={(option) => {
        const route = routeByLabel.get(option);
        if (route === HOLDS_ROUTE) {
          // Issue #454 (@dylan219 2026-07-22): reaching Hold Information FROM a
          // security page must work pre-medium and return the agent to THIS
          // page. Carry `fromSecurity` + `returnTo` (the current security route)
          // so the Account Holds screen reads holds via the identified-gated
          // `/security/holds` twin and shows a "Back to security" link to here
          // — mirroring #358's "Check Transactions" round trip. Any OTHER entry
          // to Account Holds (e.g. the Dashboard link) omits this state and
          // stays on the medium-gated `/accounts/holds` path.
          navigate(route, {
            state: { fromSecurity: true, returnTo: location.pathname },
          });
        } else if (route === CUSTOMER_NOTES_ROUTE) {
          // Issue #663 (CCRS-3448): reaching Customer Notes FROM a security page
          // should present THIS security process menu on the Customer Notes
          // screen (so the agent can swivel back into the security flow) rather
          // than the default servicing process menu. Carry `fromSecurity`, the
          // originating security route (`returnTo`), and this page's screen id +
          // mode so the Customer Notes screen rebuilds the SAME security menu
          // (excluding the originating page, correct medium/high set) and offers
          // a "Back to security" exit to here. Any OTHER entry to Customer Notes
          // (e.g. the servicing process menu, direct nav) omits this state and
          // renders the default servicing process menu unchanged.
          navigate(route, {
            state: {
              fromSecurity: true,
              returnTo: location.pathname,
              securityScreenId: currentId,
              securityMode: mode,
            },
          });
        } else if (route) {
          navigate(route);
        } else {
          toast.info(`${option} is not yet available.`);
        }
      }}
    />
  );
}
